import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'components/ui/Button'
import {
  ArrowRight,
  Check,
  Minus,
  Plus,
  RefreshCw,
  Replace,
  RotateCcw,
  Sparkles,
} from 'lucide-react'

export const REGENERATION_FORM_FIELDS = [
  'job_title',
  'department',
  'contract_type',
  'experience_level',
  'education_level',
  'description_text',
  'requirements',
  'main_missions',
  'soft_skills',
  'benefits',
  'languages',
  'certifications',
  'remote_allowed',
] as const

export type RegenerationFormField = (typeof REGENERATION_FORM_FIELDS)[number]

const FIELD_LABELS: Record<string, { fr: string; en: string }> = {
  job_title: { fr: 'Titre du poste', en: 'Job title' },
  department: { fr: 'Département', en: 'Department' },
  contract_type: { fr: 'Type de contrat', en: 'Contract type' },
  experience_level: { fr: 'Expérience', en: 'Experience' },
  education_level: { fr: "Niveau d'études", en: 'Education level' },
  description_text: { fr: 'Description', en: 'Description' },
  description: { fr: 'Description', en: 'Description' },
  requirements: { fr: 'Compétences techniques', en: 'Technical skills' },
  soft_skills: { fr: 'Soft skills', en: 'Soft skills' },
  main_missions: { fr: 'Missions', en: 'Responsibilities' },
  benefits: { fr: 'Souhaitables (nice to have)', en: 'Nice to have' },
  languages: { fr: 'Langues', en: 'Languages' },
  certifications: { fr: 'Certifications', en: 'Certifications' },
  remote_allowed: { fr: 'Télétravail', en: 'Remote work' },
}

export type QualityChangeItem = {
  field?: string
  action?: string
  current_value?: unknown
  suggested_value?: unknown
  reason?: string
  requires_input?: boolean
  label_fr?: string
  label_en?: string
  instruction?: string
  target_fields?: string[]
}

export type RegenerationQualityAnalysis = {
  strengths?: QualityChangeItem[]
  weaknesses?: QualityChangeItem[]
  recommendations?: QualityChangeItem[]
  directions?: QualityChangeItem[]
}

type Props = {
  quality: RegenerationQualityAnalysis
  disabled?: boolean
  baselineOffer?: Record<string, unknown> | null
  /** Live résumé values — proposals are treated as already applied when omitted. */
  currentOffer?: Record<string, unknown> | null
  onRetryAnalysis?: () => void
  /** Open « Modifier avec l’IA » on the résumé field — never dump values into the draft. */
  onImproveWithAI: (field: string, instruction: string) => void
  /** Apply a concrete quality suggestion into the résumé draft. */
  onApplyChange?: (field: string, value: unknown) => void
}

const canonicalizeField = (field: unknown): string => {
  const value = String(field || '').trim()
  return value === 'description' ? 'description_text' : value
}

const fieldLabel = (field: string, language: string): string => {
  const locale = language.startsWith('fr') ? 'fr' : 'en'
  return FIELD_LABELS[field]?.[locale] || field.replace(/_/g, ' ')
}

const valuesLookEqual = (left: unknown, right: unknown, field?: string): boolean => {
  if (left === right) return true
  if (field === 'experience_level') {
    const leftText = String(left ?? '').trim()
    const rightText = String(right ?? '').trim()
    if (/^\d+$/.test(leftText) && /^\d+$/.test(rightText)) {
      return leftText === rightText
    }
  }
  try {
    return JSON.stringify(left) === JSON.stringify(right)
  } catch {
    return String(left) === String(right)
  }
}

const parseStoredContainer = (value: string): unknown | null => {
  const trimmed = value.trim()
  if (!trimmed || (trimmed[0] !== '{' && trimmed[0] !== '[')) return null
  if ((trimmed[0] === '{' && !trimmed.endsWith('}')) || (trimmed[0] === '[' && !trimmed.endsWith(']'))) return null
  try {
    const parsed = JSON.parse(trimmed)
    return parsed && typeof parsed === 'object' ? parsed : null
  } catch {
    return null
  }
}

const dedupeLabels = (items: string[]): string[] => {
  const seen = new Set<string>()
  const result: string[] = []
  items.forEach((item) => {
    const label = item.trim()
    if (!label) return
    const key = label.toLocaleLowerCase('fr')
    if (seen.has(key)) return
    seen.add(key)
    result.push(label)
  })
  return result
}

/** Show skill buckets and other stored objects as a plain list, never as JSON or field names. */
const collectDisplayItems = (value: unknown): string[] => {
  if (value === null || value === undefined || value === '') return []
  if (typeof value === 'string') {
    const parsed = parseStoredContainer(value)
    if (parsed) return collectDisplayItems(parsed)
    const trimmed = value.trim()
    return trimmed ? [trimmed] : []
  }
  if (typeof value === 'number' || typeof value === 'boolean') return [String(value)]
  if (Array.isArray(value)) {
    return dedupeLabels(value.flatMap((item) => collectDisplayItems(item)))
  }
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>
    const entries = Object.entries(obj)
    if (entries.some(([, entry]) => Array.isArray(entry))) {
      const mentioned: string[] = []
      const unmentioned: string[] = []
      const other: string[] = []
      entries.forEach(([key, entry]) => {
        if (!Array.isArray(entry)) return
        const items = collectDisplayItems(entry)
        const keyName = key.toLowerCase()
        if (keyName.includes('non_mention') || keyName.includes('non-mention')) unmentioned.push(...items)
        else if (keyName.includes('mention')) mentioned.push(...items)
        else other.push(...items)
      })
      return dedupeLabels([...mentioned, ...unmentioned, ...other])
    }
    if (obj.language || obj.level) {
      const language = String(obj.language || obj.name || '').trim()
      const level = String(obj.level || '').trim()
      return language ? [`${language}${level ? `: ${level}` : ''}`] : []
    }
    const label = String(obj.label || obj.name || obj.value || '').trim()
    if (label) return [label]
    return dedupeLabels(Object.values(obj).flatMap((entry) => collectDisplayItems(entry)))
  }
  return []
}

const LIST_FIELDS = new Set([
  'requirements',
  'soft_skills',
  'benefits',
  'main_missions',
  'certifications',
  'languages',
])

const splitTopLevelCommas = (value: string): string[] => {
  const parts: string[] = []
  let current = ''
  let depth = 0
  for (const char of value) {
    if (char === '(' || char === '[') depth += 1
    if ((char === ')' || char === ']') && depth > 0) depth -= 1
    if (char === ',' && depth === 0) {
      parts.push(current)
      current = ''
      continue
    }
    current += char
  }
  parts.push(current)
  return parts.map((part) => part.trim()).filter(Boolean)
}

const itemsForListField = (value: unknown): string[] => {
  if (typeof value === 'string' && parseStoredContainer(value) === null) {
    const lines = value.split(/\r?\n+/).map((line) => line.trim()).filter(Boolean)
    if (lines.length > 1) return dedupeLabels(lines)
    if (value.includes(',')) return dedupeLabels(splitTopLevelCommas(value))
  }
  return collectDisplayItems(value)
}

const formatValue = (value: unknown, language: string): string => {
  const locale = language.startsWith('fr') ? 'fr' : 'en'
  if (value === null || value === undefined || value === '') {
    return '—'
  }
  if (value === 'Valeur à préciser') {
    return locale === 'fr' ? 'valeur à préciser' : 'value required'
  }
  if (typeof value === 'boolean') return value ? (locale === 'fr' ? 'Oui' : 'Yes') : (locale === 'fr' ? 'Non' : 'No')
  const items = collectDisplayItems(value)
  const isContainer = Array.isArray(value)
    || (value !== null && typeof value === 'object')
    || (typeof value === 'string' && parseStoredContainer(value) !== null)
  if (isContainer && items.length === 0) return locale === 'fr' ? '(vide)' : '(empty)'
  if (items.length === 0) return String(value)
  return items.join(', ')
}

const resolveTargetField = (item: QualityChangeItem): string => {
  const fromField = canonicalizeField(item.field)
  if (REGENERATION_FORM_FIELDS.includes(fromField as RegenerationFormField)) return fromField
  const fromTargets = (item.target_fields || [])
    .map(canonicalizeField)
    .find((field) => REGENERATION_FORM_FIELDS.includes(field as RegenerationFormField))
  return fromTargets || ''
}

const listDelta = (current: unknown, suggested: unknown, mode: 'add' | 'remove'): unknown[] => {
  const toList = (value: unknown): string[] => itemsForListField(value)
  const currentItems = toList(current)
  const suggestedItems = toList(suggested)
  const currentKeys = new Set(currentItems.map((item) => item.toLowerCase()))
  if (mode === 'add') {
    const onlyNew = suggestedItems.filter((item) => !currentKeys.has(item.toLowerCase()))
    return onlyNew.length > 0 ? onlyNew : suggestedItems
  }
  // remove: prefer suggested items that exist in current; else suggested as-is
  const exact = suggestedItems.filter((item) => currentKeys.has(item.toLowerCase()))
  return exact.length > 0 ? exact : suggestedItems
}

const ValueList = ({ items }: { items: string[] }) => (
  <ul className="space-y-1">
    {items.map((entry, index) => (
      <li key={`${entry}-${index}`} className="flex items-start gap-2 text-sm leading-5">
        <span className="mt-0.5 text-primary">•</span>
        <span className="min-w-0 break-words [overflow-wrap:anywhere]">{entry}</span>
      </li>
    ))}
  </ul>
)

const classifyListEdit = (current: unknown, suggested: unknown): 'add' | 'remove' | 'replace' => {
  const before = itemsForListField(current)
  const after = itemsForListField(suggested)
  const beforeKeys = new Set(before.map((item) => item.toLowerCase()))
  const afterKeys = new Set(after.map((item) => item.toLowerCase()))
  const added = after.filter((item) => !beforeKeys.has(item.toLowerCase()))
  const removed = before.filter((item) => !afterKeys.has(item.toLowerCase()))
  if (added.length > 0 && removed.length === 0) return 'add'
  if (removed.length > 0 && added.length === 0) return 'remove'
  return 'replace'
}

const changeDisplay = (item: QualityChangeItem, language: string) => {
  const locale = language.startsWith('fr') ? 'fr' : 'en'
  const declared = String(item.action || 'replace')
  const field = canonicalizeField(item.field)
  const asList = LIST_FIELDS.has(field)
  const before = formatValue(item.current_value, language)
  const beforeItems = asList ? itemsForListField(item.current_value) : []
  const isPlaceholder =
    item.suggested_value === 'Valeur à préciser'
    || item.requires_input === true
  const action = asList && declared === 'replace' && !isPlaceholder
    ? classifyListEdit(item.current_value, item.suggested_value)
    : declared
  const removedOnly = action === 'remove' && declared === 'replace'
    ? beforeItems.filter((entry) => {
      const kept = new Set(itemsForListField(item.suggested_value).map((value) => value.toLowerCase()))
      return !kept.has(entry.toLowerCase())
    })
    : null
  const addedOnly = action === 'add' && declared === 'replace'
    ? itemsForListField(item.suggested_value).filter((entry) => {
      const currentKeys = new Set(beforeItems.map((value) => value.toLowerCase()))
      return !currentKeys.has(entry.toLowerCase())
    })
    : null
  const afterSource = removedOnly
    ? removedOnly
    : addedOnly
      ? addedOnly
      : action === 'add'
        ? listDelta(item.current_value, item.suggested_value, 'add')
        : action === 'remove'
          ? listDelta(item.current_value, item.suggested_value, 'remove')
          : item.suggested_value
  const afterItems = asList && !isPlaceholder ? itemsForListField(afterSource) : []

  return {
    action,
    beforeLabel: tFallback(locale, 'Valeur actuelle', 'Current value'),
    before,
    beforeItems,
    afterLabel: action === 'add'
      ? tFallback(locale, 'À ajouter', 'To add')
      : action === 'remove'
        ? tFallback(locale, 'À retirer', 'To remove')
        : tFallback(locale, 'Nouvelle valeur', 'New value'),
    after: isPlaceholder
      ? tFallback(locale, 'Utilisez « Modifier avec l’IA »', 'Use “Edit with AI”')
      : formatValue(afterSource, language),
    afterItems,
  }
}

const tFallback = (locale: string, fr: string, en: string) => (locale === 'fr' ? fr : en)

const actionMeta = (action: string, language: string) => {
  const locale = language.startsWith('fr') ? 'fr' : 'en'
  if (action === 'add') {
    return {
      label: locale === 'fr' ? 'Ajouter' : 'Add',
      className: 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200',
      Icon: Plus,
    }
  }
  if (action === 'remove') {
    return {
      label: locale === 'fr' ? 'Retirer' : 'Remove',
      className: 'border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200',
      Icon: Minus,
    }
  }
  return {
    label: locale === 'fr' ? 'Remplacer' : 'Replace',
    className: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100',
    Icon: Replace,
  }
}

const isActionableChange = (item: QualityChangeItem): boolean => {
  if (item.action === 'keep') return false
  if (!resolveTargetField(item)) return false
  if (
    item.action === 'remove'
    && (item.suggested_value == null
      || item.suggested_value === ''
      || (Array.isArray(item.suggested_value) && item.suggested_value.length === 0))
  ) {
    return false
  }
  return true
}

export const buildAiInstruction = (item: QualityChangeItem, language: string): string => {
  const locale = language.startsWith('fr') ? 'fr' : 'en'
  const field = resolveTargetField(item)
  const label = fieldLabel(field, language)
  const reason = String(item.reason || '').trim()
  const action = String(item.action || 'replace').trim()
  const suggested = formatValue(item.suggested_value, language)
  const hasConcreteSuggestion = Boolean(
    suggested
    && suggested !== '—'
    && item.suggested_value !== 'Valeur à préciser'
    && !item.requires_input
    && !valuesLookEqual(item.current_value, item.suggested_value, field),
  )

  const parts: string[] = []
  if (reason) {
    parts.push(reason)
  } else if (locale === 'fr') {
    parts.push(`Améliore le champ « ${label} ».`)
  } else {
    parts.push(`Improve the “${label}” field.`)
  }

  if (item.requires_input || item.suggested_value === 'Valeur à préciser') {
    parts.push(
      locale === 'fr'
        ? 'Demande ou prépare une valeur explicite sans inventer de fait métier.'
        : 'Ask for or prepare an explicit value without inventing business facts.',
    )
  } else if (hasConcreteSuggestion && action === 'add') {
    parts.push(
      locale === 'fr'
        ? `Ajoute exactement ces valeurs à la liste existante (sans supprimer les valeurs déjà présentes) : ${suggested}.`
        : `Add exactly these values to the existing list (do not remove current values): ${suggested}.`,
    )
  } else if (hasConcreteSuggestion && action === 'remove') {
    parts.push(
      locale === 'fr'
        ? `Retire exactement ces valeurs de la liste : ${suggested}.`
        : `Remove exactly these values from the list: ${suggested}.`,
    )
  } else if (hasConcreteSuggestion) {
    parts.push(
      locale === 'fr'
        ? `Remplace le champ par cette valeur proposée : ${suggested}.`
        : `Replace the field with this suggested value: ${suggested}.`,
    )
  } else if (item.instruction && String(item.instruction).trim()) {
    parts.push(String(item.instruction).trim())
  } else {
    parts.push(
      locale === 'fr'
        ? 'Réécris clairement ce champ : texte différent, faits métier conservés, sans invention.'
        : 'Clearly rewrite this field: different wording, keep business facts, no invention.',
    )
  }

  return parts.join(' ')
}

/** Collect field changes from recommendations (and legacy weaknesses). */
export const collectQualityChanges = (quality: RegenerationQualityAnalysis): QualityChangeItem[] => {
  const seen = new Set<string>()
  const changes: QualityChangeItem[] = []
  for (const item of [...(quality.recommendations || []), ...(quality.weaknesses || [])]) {
    if (!item || item.action === 'keep') continue
    const field = resolveTargetField(item)
    if (!field || seen.has(field)) continue
    if (
      item.action === 'remove'
      && (item.suggested_value == null
        || item.suggested_value === ''
        || (Array.isArray(item.suggested_value) && item.suggested_value.length === 0))
    ) {
      continue
    }
    if (valuesLookEqual(item.current_value, item.suggested_value, field)) {
      continue
    }
    seen.add(field)
    changes.push(item)
  }
  return changes
}

const toComparableList = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (item && typeof item === 'object') {
        const obj = item as Record<string, unknown>
        if (obj.language) return `${obj.language}${obj.level ? `: ${obj.level}` : ''}`
        return String(obj.label || obj.name || JSON.stringify(item))
      }
      return String(item)
    }).map((item) => item.trim()).filter(Boolean)
  }
  if (value && typeof value === 'object') {
    const nested = Object.values(value as Record<string, unknown>).flatMap((entry) => (
      Array.isArray(entry) ? entry : entry == null || entry === '' ? [] : [entry]
    ))
    return toComparableList(nested)
  }
  if (value == null || value === '') return []
  return [String(value).trim()].filter(Boolean)
}

export const resolveAppliedQualityValue = (
  item: QualityChangeItem,
  baselineValue?: unknown,
): unknown => {
  const action = String(item.action || 'replace')
  const suggested = item.suggested_value
  const current = baselineValue !== undefined ? baselineValue : item.current_value
  if (action === 'add') {
    const currentItems = toComparableList(current)
    const addItems = toComparableList(listDelta(current, suggested, 'add'))
    const seen = new Set(currentItems.map((entry) => entry.toLowerCase()))
    const merged = [...currentItems]
    for (const entry of addItems) {
      if (seen.has(entry.toLowerCase())) continue
      seen.add(entry.toLowerCase())
      merged.push(entry)
    }
    return merged
  }
  if (action === 'remove') {
    const removeKeys = new Set(
      toComparableList(listDelta(current, suggested, 'remove')).map((entry) => entry.toLowerCase()),
    )
    return toComparableList(current).filter((entry) => !removeKeys.has(entry.toLowerCase()))
  }
  if (LIST_FIELDS.has(canonicalizeField(item.field))) {
    return itemsForListField(suggested)
  }
  return suggested
}

/** Concrete values to apply in the résumé for each quality proposal. */
export const buildQualityProposalValues = (
  quality: RegenerationQualityAnalysis,
  baseline: Record<string, unknown> | null | undefined,
): Record<string, { value: unknown; action: string }> => {
  const values: Record<string, { value: unknown; action: string }> = {}
  for (const item of collectQualityChanges(quality)) {
    if (item.requires_input || item.suggested_value === 'Valeur à préciser') continue
    const field = resolveTargetField(item)
    if (!field) continue
    const action = String(item.action || 'replace').trim() || 'replace'
    values[field] = {
      value: resolveAppliedQualityValue(item, baseline?.[field]),
      action,
    }
  }
  return values
}

/** Overlay quality proposals onto an offer so the résumé starts from the applied state. */
export const applyQualityProposalsToOffer = (
  offer: Record<string, unknown> | null | undefined,
  proposals: Record<string, { value: unknown; action: string }>,
): Record<string, unknown> => {
  let next: Record<string, unknown>
  try {
    next = JSON.parse(JSON.stringify(offer || {}))
  } catch {
    next = { ...(offer || {}) }
  }
  Object.entries(proposals || {}).forEach(([field, proposal]) => {
    next[field] = proposal.value
  })
  return next
}

export function OfferRegenerationWorkspace({
  quality,
  disabled = false,
  baselineOffer = null,
  currentOffer = null,
  onRetryAnalysis,
  onImproveWithAI,
  onApplyChange,
}: Props) {
  const { t, i18n } = useTranslation()
  const language = i18n.language || 'fr'

  const changes = useMemo(() => collectQualityChanges(quality), [quality])

  const openAiAssist = (item: QualityChangeItem) => {
    const field = resolveTargetField(item)
    if (!field) return
    onImproveWithAI(field, buildAiInstruction(item, language))
  }

  const applyChange = (item: QualityChangeItem) => {
    const field = resolveTargetField(item)
    if (!field || !onApplyChange) return
    const baselineValue = baselineOffer ? baselineOffer[field] : item.current_value
    onApplyChange(field, resolveAppliedQualityValue(item, baselineValue))
  }

  const resetChange = (item: QualityChangeItem) => {
    const field = resolveTargetField(item)
    if (!field || !onApplyChange) return
    const baselineValue = baselineOffer ? baselineOffer[field] : item.current_value
    onApplyChange(field, baselineValue ?? '')
  }

  return (
    <div className="rounded-xl border border-primary/20 bg-gradient-to-br from-primary/10 via-background to-primary/5 p-4 shadow-sm">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-bold text-foreground">
            {t('aiAssistant.proposedFieldChanges', 'Modifications proposées')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t(
              'aiAssistant.proposedFieldChangesHint',
              'Les propositions sont déjà appliquées. Réinitialisez un champ pour revenir à l\'offre d\'origine.',
            )}
          </p>
        </div>
        {onRetryAnalysis ? (
          <Button
            size="sm"
            variant="outline"
            disabled={disabled}
            onClick={onRetryAnalysis}
          >
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            {t('aiAssistant.reanalyzeOffer', 'Réanalyser')}
          </Button>
        ) : null}
      </div>

      {changes.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border/70 bg-background/60 px-3 py-4 text-sm text-muted-foreground">
          {t('aiAssistant.noFieldChangesYet', 'Aucun champ à modifier pour le moment.')}
        </p>
      ) : (
        <ul className="space-y-3">
          {changes.map((item, index) => {
            const field = resolveTargetField(item)
            const display = changeDisplay(item, language)
            const action = display.action
            const meta = actionMeta(action, language)
            const ActionIcon = meta.Icon
            const reason = String(item.reason || '').trim()
            const baselineValue = baselineOffer ? baselineOffer[field] : item.current_value
            const proposalValue = resolveAppliedQualityValue(item, baselineValue)
            const liveValue = currentOffer && field
              ? currentOffer[field]
              : proposalValue
            const matchesProposal = (
              valuesLookEqual(liveValue, proposalValue, field)
              || formatValue(liveValue, language) === formatValue(proposalValue, language)
            )
            const matchesBaseline = (
              valuesLookEqual(liveValue, baselineValue, field)
              || formatValue(liveValue, language) === formatValue(baselineValue, language)
            )
            const canApplyConcrete = Boolean(
              onApplyChange
              && isActionableChange(item)
              && item.suggested_value !== 'Valeur à préciser'
              && !item.requires_input
              && !matchesProposal,
            )
            const canResetChange = Boolean(
              onApplyChange
              && isActionableChange(item)
              && !matchesBaseline,
            )

            return (
              <li
                key={`${field}-${index}`}
                className="rounded-lg border border-border/70 bg-background/80 p-3 shadow-sm"
              >
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {fieldLabel(field, language)}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${meta.className}`}
                  >
                    <ActionIcon className="h-3 w-3" />
                    {meta.label}
                  </span>
                  <div className="ml-auto flex flex-wrap items-center gap-1">
                    {canApplyConcrete && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={disabled}
                        className="h-7 border-emerald-300 bg-emerald-50 px-2 text-xs text-emerald-900 hover:bg-emerald-100 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-100"
                        onClick={() => applyChange(item)}
                      >
                        <Check className="mr-1 h-3.5 w-3.5" />
                        {t('aiAssistant.applyFieldChange', 'Appliquer')}
                      </Button>
                    )}
                    {canResetChange && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled={disabled}
                        className="h-7 px-2 text-xs"
                        onClick={() => resetChange(item)}
                      >
                        <RotateCcw className="mr-1 h-3.5 w-3.5" />
                        {t('aiAssistant.resetFieldChange', 'Réinitialiser')}
                      </Button>
                    )}
                    {isActionableChange(item) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={disabled}
                        className="h-7 px-2 text-xs"
                        onClick={() => openAiAssist(item)}
                        title={t('aiAssistant.editWithAI', 'Modifier avec l’IA')}
                      >
                        <Sparkles className="mr-1 h-3.5 w-3.5" />
                        {t('aiAssistant.editWithAI', 'Modifier avec l’IA')}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="grid gap-2 sm:grid-cols-[1fr_auto_1fr] sm:items-start">
                  <div className="rounded-md border border-border/60 bg-muted/30 px-2.5 py-2">
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                      {display.beforeLabel}
                    </p>
                    {display.beforeItems.length > 0 ? (
                      <ValueList items={display.beforeItems} />
                    ) : (
                      <p className="whitespace-pre-wrap break-words text-sm text-foreground/90">
                        {display.before}
                      </p>
                    )}
                  </div>
                  <div className="hidden justify-center pt-6 text-muted-foreground sm:flex">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                  <div
                    className={`rounded-md border px-2.5 py-2 ${
                      action === 'remove'
                        ? 'border-rose-200/80 bg-rose-50/70 dark:border-rose-900/50 dark:bg-rose-950/20'
                        : action === 'add'
                          ? 'border-emerald-200/80 bg-emerald-50/70 dark:border-emerald-900/50 dark:bg-emerald-950/20'
                          : 'border-primary/25 bg-primary/5'
                    }`}
                  >
                    <p
                      className={`mb-1 text-[10px] font-bold uppercase tracking-wide ${
                        action === 'remove'
                          ? 'text-rose-700 dark:text-rose-300'
                          : action === 'add'
                            ? 'text-emerald-700 dark:text-emerald-300'
                            : 'text-primary'
                      }`}
                    >
                      {display.afterLabel}
                    </p>
                    {display.afterItems.length > 0 ? (
                      <ValueList items={display.afterItems} />
                    ) : (
                      <p className="whitespace-pre-wrap break-words text-sm font-medium text-foreground">
                        {display.after}
                      </p>
                    )}
                  </div>
                </div>

                {reason ? (
                  <p className="mt-2 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground/80">
                      {t('aiAssistant.changeReason', 'Raison')}
                      {' : '}
                    </span>
                    {reason}
                  </p>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
